import {
  CascaderField,
  Form,
  type OperationType,
  SelectField,
  SubmitButton,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  useAccountingTypeOptions,
  useExpenditurePurposeOfSettlementStatisticsOptions,
  useExpenditurePurposeOptions,
} from '../../hooks';
import { ExpenditurePurposeOfSettlementStatisticsPresetFormSchema } from '../../models';

type FormValues = z.infer<
  typeof ExpenditurePurposeOfSettlementStatisticsPresetFormSchema
>;

type Props = {
  type: OperationType;
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
};

export const ExpenditurePurposeOfSettlementStatisticsPresetForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      ExpenditurePurposeOfSettlementStatisticsPresetFormSchema,
    ),
  });

  const accountingOptions = useAccountingTypeOptions();
  const accountingId = watch('accountingId');
  const expenditurePurposeOptions = useExpenditurePurposeOptions({
    accountingId,
    showCode: true,
  });
  const expenditurePurposeOfSettlementStatisticsOptions =
    useExpenditurePurposeOfSettlementStatisticsOptions({ showCode: true });

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  useEffect(() => {
    if (accountingId) {
      setValue('expenditurePurposeIds', []);
    }
  }, [accountingId]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <SelectField
        name="accountingId"
        label={t('accounting-name')}
        control={control}
        errors={errors}
        options={accountingOptions.data}
        required
      />

      <CascaderField
        name="expenditurePurposeIds"
        label={t('expenditure-purpose')}
        control={control}
        errors={errors}
        options={expenditurePurposeOptions.data}
        required
      />

      <CascaderField
        name="expenditurePurposeOfSettlementStatisticsIds"
        label={t('expenditure-purpose-of-settlement-statistics')}
        control={control}
        errors={errors}
        options={expenditurePurposeOfSettlementStatisticsOptions.data}
        required
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
